// Admin · Logs — unified observability across all components (frontend,
// the API, pixel-worker, batch-worker), backed by the PostgreSQL `app_logs` table.
//
//   • KPI strip  — total / errors / warns / error-rate + per-component volume
//   • Stream     — live tail (5s poll), server-side filters, trace drill-down
//   • Groups     — Sentry-style groups collapsed by signature, of the LEVEL
//                  filter's level (warnings and errors with none)
//   • Health     — per-component status: volume, errors, last-seen freshness
//
// Data: useLogStats (KPIs+health), useAdminLogs (stream, server-filtered),
// useLogGroups (groups). All derive DIRECTLY from the query cache so a cold
// reload and a warm navigate render identically.

import { ResponsiveKpis } from '@/components/responsive-kpis';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icons } from '@/components/primitives';
import { Dropdown } from '@/components/dropdowns';
import { CPPanel, CPSparkBtn, useExplainPanel } from '@/components/copilot';
import { TRACE_CITE_CAP_NOTE, citedLineNumbers, resolvableClaims, traceCiteDegraded, traceExplainRequest, traceLineNo } from '@/lib/copilot';
import { useAdminLogs, useCopilotStatus, useLogStats, useLogGroups, useLogArchive, useLogArchiveObject, useHealthSummary, type LogFilters } from '@/api/hooks';
import type { AppLogLine, LogGroupsPage, LogStats } from '@/api/types';
import { bandColor, bandFor, useSystemMetric } from '@/lib/system-metrics';
import { LogRequest } from '@/components/log-request';
import { isErrorKind, parseLogContext, streamStateFor } from '@/lib/log-line';
import { QueryErrorNote } from '@/components/error-panel';
import { fmtClockMs, fmtStampEt, fmtDateInput, TZ_LABEL } from '@/lib/datetime';
import { fmtRelative } from './admin-user-drawer';
import { PageActions } from '@/components/page-actions';
import { componentDetail, componentLabel } from '@/lib/chip-state';

interface FilterOption {
  id: string;
  label: string;
}

const WINDOWS: Array<FilterOption & { hours: number }> = [
  { id: '1h', label: 'Last hour', hours: 1 },
  { id: '24h', label: 'Last 24h', hours: 24 },
  { id: '7d', label: 'Last 7 days', hours: 168 },
  { id: '30d', label: 'Last 30 days', hours: 720 },
];
const DEFAULT_WINDOW = '24h';

const COMPONENTS: FilterOption[] = [
  { id: 'all', label: 'All components' },
  { id: 'frontend', label: 'Frontend' },
  { id: 'api_worker', label: componentLabel('api_worker') },
  { id: 'pixel_worker', label: componentLabel('pixel_worker') },
  { id: 'batch_worker', label: componentLabel('batch_worker') },
];

const LEVELS: FilterOption[] = [
  { id: 'all', label: 'All levels' },
  { id: 'debug', label: 'Debug' },
  { id: 'info', label: 'Info' },
  { id: 'warn', label: 'Warn' },
  { id: 'error', label: 'Error' },
];

type LogTab = 'stream' | 'groups' | 'health' | 'archive';
const TABS: ReadonlyArray<{ id: LogTab; label: string }> = [
  { id: 'stream', label: 'Stream' },
  { id: 'groups', label: 'Groups' },
  { id: 'health', label: 'Health' },
  { id: 'archive', label: 'Archive' },
];

/** What the Groups tab is grouping, in its label and its empty line. */
interface GroupScope {
  /** After "Groups · " in the tab. */
  label: string;
  /** The empty line, "in this window" like the stream's. */
  empty: string;
}

// The Groups tab reads the stream's LEVEL filter: a level groups exactly that
// level, and "All levels" sends none, which the server answers with warnings
// and errors only. The tab used to say "Error groups" whatever it held, so
// Level = Info listed info groups under that name and, empty, still said "No
// error or warning groups".
const GROUPS_WITHOUT_LEVEL: GroupScope = {
  label: 'Warnings and errors',
  empty: 'No warning or error groups in this window. 🎉',
};
const GROUP_SCOPES: Partial<Record<string, GroupScope>> = {
  all: GROUPS_WITHOUT_LEVEL,
  debug: { label: 'Debug', empty: 'No debug groups in this window.' },
  info: { label: 'Info', empty: 'No info groups in this window.' },
  warn: { label: 'Warnings', empty: 'No warning groups in this window. 🎉' },
  error: { label: 'Errors', empty: 'No error groups in this window. 🎉' },
};
const groupScope = (level: string): GroupScope => GROUP_SCOPES[level] ?? GROUPS_WITHOUT_LEVEL;

// Component identity colours. They are read as small text (the stream's
// source column, the groups' component cell), so they are the `--log-comp-*`
// tokens, pinned at AA contrast in both themes, not the raw chart palette.
const COMP_COLOR: Partial<Record<string, string>> = {
  frontend: 'var(--log-comp-frontend)',
  api_worker: 'var(--log-comp-api)',
  pixel_worker: 'var(--log-comp-pixel)',
  batch_worker: 'var(--log-comp-batch)',
};
const compColor = (component: string | null | undefined): string | undefined =>
  component ? COMP_COLOR[component] : undefined;
const LEVEL_COLOR: Partial<Record<string, string>> = { debug: 'var(--ink-3)', info: 'var(--chart-3)', warn: 'var(--warn)', error: 'var(--bad)' };
// Health tiles read their pill + border color from the server's HealthStatus,
// not from local log-freshness math — so the tile, chip, and alert engine all
// mean the same "green". Quiet = healthy but idle (no positive signal owed).
interface HealthUi {
  color: string;
  label: string;
}
const HEALTH_QUIET: HealthUi = { color: 'var(--ink-3)', label: 'quiet' };
const HEALTH_UI: Partial<Record<string, HealthUi>> = {
  up: { color: 'var(--good)', label: 'healthy' },
  degraded: { color: 'var(--bad)', label: 'errors' },
  down: { color: 'var(--bad)', label: 'down' },
  quiet: HEALTH_QUIET,
};
const compLabel = (c: string | null | undefined): string => (c ? componentLabel(c) : 'unknown');

type LogRow = AppLogLine & { _id: string };
type LogGroup = LogGroupsPage['groups'][number];
type LogTotals = Pick<LogStats, 'total' | 'errors' | 'warns' | 'components'>;
const NO_STATS: LogTotals = { total: 0, errors: 0, warns: 0, components: [] };

// Newest-first server page → chronological, with a synthesized stable key. The
// wire AppLogLine carries no id, so key on ts|component|message — plus an
// occurrence counter so repeated identical lines (a retry storm emitting the
// same message within one millisecond) never collide as React keys / expand
// ids. The first occurrence keeps the bare form, so expansion survives polls.
const withLineIds = (lines: ReadonlyArray<AppLogLine>): LogRow[] => {
  const seen = new Map<string, number>();
  return lines
    .slice()
    .reverse()
    .map((l) => {
      const base = `${l.ts}|${l.component ?? l.source}|${l.message}`;
      const n = (seen.get(base) ?? 0) + 1;
      seen.set(base, n);
      return { ...l, _id: n > 1 ? `${base}#${n}` : base };
    });
};

type LogsViewProps = {
  setView?: unknown;
  initialSignature?: string | null;
  initialTrace?: string | null;
  initialComponent?: string | null;
  initialWindow?: string | null;
};

function LogsView({ setView: _setView, initialSignature = null, initialTrace = null, initialComponent = null, initialWindow = null }: LogsViewProps) {
  const [tab, setTab] = useState<LogTab>('stream');
  const [win, setWin] = useState<string>(() => (
    initialWindow && WINDOWS.some((w) => w.id === initialWindow) ? initialWindow : DEFAULT_WINDOW
  ));
  const [component, setComponent] = useState<string>(() => (
    initialComponent && COMPONENTS.some((candidate) => candidate.id === initialComponent) ? initialComponent : 'all'
  ));
  const [level, setLevel] = useState<string>('all');
  const [q, setQ] = useState('');
  const [errorKind, setErrorKind] = useState('');
  const [trace, setTrace] = useState<string | null>(initialTrace); // active trace_id drill-down
  const [signature, setSignature] = useState<string | null>(initialSignature); // exact error-group drill-down
  const [paused, setPaused] = useState(false);
  const [frozen, setFrozen] = useState<LogRow[] | null>(null);
  const [clearBefore, setClearBefore] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  // Archive tab state.
  const [archiveDate, setArchiveDate] = useState(() => fmtDateInput(Date.now()));
  // One hour of a day, or '' for the whole day. One listing returns at most
  // 1,000 files; a busy day always exceeds that and a busy hour can too.
  const [archiveHour, setArchiveHour] = useState('');
  const [archiveKey, setArchiveKey] = useState<string | null>(null);

  // TanStack Router can update search params without remounting this screen.
  // Re-apply a new alert deep link while leaving ordinary local interactions
  // (filters, group clicks, trace clicks) under this component's control.
  useEffect(() => {
    setSignature(initialSignature ?? null);
    setTrace(initialTrace ?? null);
    if (initialComponent && COMPONENTS.some((candidate) => candidate.id === initialComponent)) {
      setComponent(initialComponent);
    }
    if (initialWindow && WINDOWS.some((w) => w.id === initialWindow)) setWin(initialWindow);
    if (initialSignature || initialTrace || initialComponent) {
      setClearBefore(0);
      setPaused(false);
      setFrozen(null);
      setTab('stream');
    }
  }, [initialSignature, initialTrace, initialComponent, initialWindow]);

  const windowHours = WINDOWS.find((w) => w.id === win)?.hours ?? 24;

  // Copilot §2c — trace narration. The panel renders BETWEEN the banner and
  // the stream; hovering a claim highlights its cited lines and dims the rest;
  // clicking a `line N` chip scrolls the stream container (scrollTop math,
  // never scrollIntoView) and flashes the line ~1.4s. Refs `lN` map to line
  // numbers via the evidence label ("line N") against the RENDERED trace list.
  const copilotOn = useCopilotStatus().data?.enabled === true;
  const ex = useExplainPanel();
  const [hoverClaim, setHoverClaim] = useState<number | null>(null);
  const [flashLine, setFlashLine] = useState<number | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const flashT = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (flashT.current) clearTimeout(flashT.current); }, []);
  const closeExplain = () => { ex.close(); setHoverClaim(null); };

  // KPIs + per-component log volume (30s poll).
  const statsQuery = useLogStats(windowHours);
  const stats: LogTotals = statsQuery.data ?? NO_STATS;
  // A KPI strip that knows nothing must not assert zero errors — that is a
  // claim, and it is the opposite of the truth when the query is failing.
  const statsUnknown = statsQuery.isError && !statsQuery.data;
  const kpi = (value: string): string => (statsUnknown ? '—' : value);
  const errorRate = stats.total > 0 ? (stats.errors / stats.total) * 100 : 0;
  // Central `health.error_rate_pct` band (was a hardcoded >5).
  const errM = useSystemMetric('health.error_rate_pct');
  const errorRateColor = errM ? bandColor(bandFor(errM, errorRate)) : (errorRate > 5 ? 'var(--bad)' : 'var(--good)');

  // Authoritative component health (heartbeat + error rates) — drives the
  // Health-tab pill/color; log volume stays numeric-only.
  const healthComponents = useHealthSummary().data?.components ?? [];

  // Stream — server-side filtered (accurate over the whole window, not just the
  // fetched page). A trace drill-down overrides the other filters.
  const streamFilters: LogFilters = trace
    ? { trace_id: trace }
    : signature
      ? { signature, window: win }
      : {
        window: win,
        ...(component !== 'all' ? { component } : {}),
        ...(level !== 'all' ? { level } : {}),
        ...(q.trim() ? { q: q.trim() } : {}),
        // Only when it is a shape the server will accept — an invalid value
        // must never be dropped silently into an unfiltered stream.
        ...(isErrorKind(errorKind.trim()) ? { error_kind: errorKind.trim() } : {}),
      };
  const logsQuery = useAdminLogs(streamFilters);
  const page = logsQuery.data;
  const errorKindInvalid = errorKind.trim().length > 0 && !isErrorKind(errorKind.trim());

  // Rows: pure function of the query cache; newest-first from server → reverse
  // to chronological (newest at bottom). Synthesize a stable key.
  const live = useMemo(() => withLineIds(page?.lines ?? []), [page]);
  const displayed = useMemo(() => {
    const base = paused && frozen ? frozen : live;
    return clearBefore ? base.filter((l) => new Date(l.ts).getTime() > clearBefore) : base;
  }, [paused, frozen, live, clearBefore]);

  const streamState = streamStateFor({
    isError: logsQuery.isError,
    isLoading: logsQuery.isPending,
    rowCount: displayed.length,
    hasSignature: !!signature,
  });

  const togglePause = () => {
    if (paused) { setPaused(false); setFrozen(null); }
    else { setFrozen(live); setPaused(true); }
  };

  // Groups (only fetched/relevant on the Groups tab).
  const groupsQuery = useLogGroups(windowHours, level !== 'all' ? level : undefined);
  const groups = groupsQuery.data?.groups ?? [];
  // The server lists the most frequent groups and says when there were more.
  const groupsTruncated = groupsQuery.data?.truncated ?? false;
  const groupsScope = groupScope(level);
  const groupsLabel = `Groups · ${groupsScope.label}`;

  // Archive (cold R2 store) — list files for a date, load one on click.
  const archiveListing = useLogArchive(archiveDate, archiveHour || undefined, tab === 'archive').data;
  const archiveList = archiveListing?.entries ?? [];
  const archiveTruncated = archiveListing?.truncated ?? false;
  const archiveObjQuery = useLogArchiveObject(tab === 'archive' ? archiveKey : null);
  const archiveObj = archiveObjQuery.data;
  const archiveLines = useMemo(() => withLineIds(archiveObj?.lines ?? []), [archiveObj]);

  // Drilling a trace RESETS divergent stream state — Clear watermark and the
  // pause freeze — so the rendered list is the live chronological trace. The
  // server numbers its `lN` refs over the trace's first 50 CHRONOLOGICAL
  // lines; any client-side dropping desyncs the positional mapping.
  const drillTrace = (t: string) => {
    if (!t) return;
    closeExplain();
    setClearBefore(0);
    setPaused(false);
    setFrozen(null);
    setTrace(t);
    setTab('stream');
  };

  // Fetch-cap guard: the stream fetch returns the newest 100 lines. A trace
  // that fills the whole page may be missing its OLDEST lines, so positional
  // `line N` citations can point at the wrong rows — degrade them to inert
  // chips (no highlight/scroll) while keeping the narration panel.
  const citeDegraded = trace != null && traceCiteDegraded(displayed.length);

  // Line numbers cited by the hovered claim — mapped against the same
  // filtered claim list CPPanel renders (unresolvable claims are dropped
  // BEFORE indexing, so hover index N means the N-th visible claim).
  const citedSet = useMemo(() => {
    if (citeDegraded) return null;
    if (hoverClaim == null || !ex.open || ex.state !== 'done' || !ex.resp || ex.resp.degraded) return null;
    const claims = resolvableClaims(ex.resp.claims, ex.resp.evidence);
    const c = claims[hoverClaim];
    if (!c) return null;
    const set = citedLineNumbers(c, ex.resp.evidence);
    return set.size ? set : null;
  }, [hoverClaim, ex.open, ex.state, ex.resp, citeDegraded]);

  // Chip click → scroll the stream container to the cited line + ~1.4s flash.
  const scrollToLine = (ev: Parameters<typeof traceLineNo>[0]) => {
    const n = traceLineNo(ev);
    const c = streamRef.current;
    if (n == null || !c) return;
    const el = c.querySelector<HTMLElement>(`[data-line="${n}"]`);
    if (!el) return;
    const cr = c.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    // Container scrollTop math — scrollIntoView would also shift the page.
    c.scrollTop += er.top - cr.top - c.clientHeight / 2 + 40;
    setFlashLine(n);
    if (flashT.current) clearTimeout(flashT.current);
    flashT.current = setTimeout(() => setFlashLine((f) => (f === n ? null : f)), 1400);
  };

  // An error group's drill-down: its exact signature in the stream.
  const drillGroup = (g: LogGroup) => {
    setComponent(g.component);
    setLevel(g.level);
    setClearBefore(0);
    setPaused(false);
    setFrozen(null);
    setSignature(g.signature);
    setTab('stream');
    setTrace(null);
  };

  return (
    <div className="tbl-frame">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="page-hd">
        <div>
          <h1 className="page-title">Logs</h1>
        </div>
        <PageActions style={{ gap: 8 }}>
          <Dropdown
            label="WINDOW"
            value={win}
            options={WINDOWS.map((w) => ({ id: w.id, label: w.label }))}
            onChange={setWin}
            width={160}
            rightAlign
          />
          {/* Pause/Clear are disabled during a trace drill-down: dropping or
              freezing lines desyncs the trace's positional `line N` citations
              from the server's chronological numbering. Each button is its own
              child (no fragment) so PageActions can place them on a phone. */}
          {tab === 'stream' && (
            <button className="btn" data-phone="inline" onClick={togglePause} disabled={!!trace}
              title={trace ? 'Disabled during a trace drill-down — freezing the list would desync the cited line numbers' : undefined}>
              {paused ? <>{Icons.play} Resume</> : <>{Icons.pause} Pause</>}
            </button>
          )}
          {tab === 'stream' && (
            <button className="btn" onClick={() => setClearBefore(Date.now())} disabled={!!trace}
              title={trace ? 'Disabled during a trace drill-down — dropping lines would desync the cited line numbers' : undefined}>
              {Icons.x} Clear
            </button>
          )}
        </PageActions>
      </div>

      {/* ── KPI strip ──────────────────────────────────────────────── */}
      <ResponsiveKpis><div className="kpi-strip" style={{ display: 'flex' }}>
        <Kpi label="Total" value={kpi(stats.total.toLocaleString())} />
        <Kpi label="Errors" value={kpi(stats.errors.toLocaleString())} color="var(--bad)" />
        <Kpi label="Warnings" value={kpi(stats.warns.toLocaleString())} color="var(--warn)" />
        <Kpi label="Error rate" value={kpi(`${errorRate.toFixed(errorRate < 10 ? 1 : 0)}%`)} color={errorRateColor} />
        <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center', padding: '0 16px', flexWrap: 'wrap' }}>
          {stats.components.map((c) => (
            <button
              type="button"
              key={c.component}
              onClick={() => { setComponent(component === c.component ? 'all' : c.component); setTrace(null); setSignature(null); setTab('stream'); }}
              title={`${compLabel(c.component)} · ${c.errors} errors · last ${c.last_ts ? fmtRelative(+new Date(c.last_ts)) : '—'}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', cursor: 'pointer',
                borderRadius: 'var(--r)', fontSize: 11, fontFamily: 'var(--font-mono)',
                // The selected chip is marked by its component-coloured border alone: a
                // 12% tint of that colour took the red error count to 4.41:1 in the dark
                // theme (axe, F1 logs flow); on --panel every chip text passes.
                border: '1px solid ' + (component === c.component ? (compColor(c.component) ?? 'var(--line)') : 'var(--line)'),
                boxShadow: component === c.component ? 'inset 0 0 0 1px ' + (compColor(c.component) ?? 'var(--line)') : undefined,
                background: 'var(--panel)',
              }}
              aria-pressed={component === c.component}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: compColor(c.component) ?? 'var(--ink-3)' }} />
              {compLabel(c.component)}
              <span className="muted">{c.total.toLocaleString()}</span>
              {c.errors > 0 && <span style={{ color: 'var(--bad)' }}>· {c.errors}</span>}
            </button>
          ))}
        </div>
      </div></ResponsiveKpis>

      {/* ── Tabs ───────────────────────────────────────────────────── */}
      <div className="log-view-tabs" role="tablist" aria-label="Log views" style={{ display: 'flex', gap: 4, padding: '8px 22px 0', borderBottom: '1px solid var(--line)', background: 'var(--bg-elev)' }}>
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            style={{
              padding: '8px 16px', cursor: 'pointer', background: 'transparent', border: 'none',
              borderBottom: '2px solid ' + (tab === id ? 'var(--accent, var(--ink-1))' : 'transparent'),
              color: tab === id ? 'var(--ink-1)' : 'var(--ink-3)', fontWeight: tab === id ? 700 : 500, fontSize: 13,
            }}
          >
            {id === 'groups' ? groupsLabel : label}
          </button>
        ))}
      </div>

      {/* Active trace drill-down banner */}
      {trace && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 22px', background: 'color-mix(in oklch, var(--accent, #6aa9ff) 8%, var(--bg-elev))', borderBottom: '1px solid var(--line)', fontSize: 12 }}>
          <span className="muted mono" style={{ textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 10 }}>Trace</span>
          <code className="mono">{trace}</code>
          <span className="muted">· cross-component timeline</span>
          {copilotOn && (
            <CPSparkBtn label="Explain this trace" open={ex.open}
              onClick={() => (ex.open ? closeExplain() : ex.trigger(traceExplainRequest(trace)))}/>
          )}
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => { closeExplain(); setTrace(null); }}>{Icons.x} Clear trace</button>
        </div>
      )}

      {/* Exact signature drill-down remains underneath a trace so clearing a
          correlated timeline returns to the originating error group. */}
      {!trace && signature && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 22px', background: 'color-mix(in oklch, var(--bad) 7%, var(--bg-elev))', borderBottom: '1px solid var(--line)', fontSize: 12 }}>
          <span className="muted mono" style={{ textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 10 }}>Error group</span>
          <code className="mono">{signature}</code>
          <span className="muted">· exact signature match · {displayed.length} shown</span>
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setSignature(null)}>{Icons.x} Clear group</button>
        </div>
      )}

      {/* Copilot §2c — the narration panel sits BETWEEN banner and stream */}
      {trace && tab === 'stream' && copilotOn && ex.open && (
        <div style={{ padding: '12px 22px 0', flexShrink: 0 }}>
          <CPPanel resp={ex.resp} state={ex.state} kind="trace" error={ex.error}
            onClose={closeExplain} onRegenerate={ex.regenerate} regenBusy={ex.state === 'loading'}
            onScrollTo={citeDegraded ? undefined : scrollToLine} chipHov
            scrollNote={citeDegraded ? TRACE_CITE_CAP_NOTE : null}
            hoveredClaim={hoverClaim} onClaimHover={setHoverClaim}/>
        </div>
      )}

      {/* ── STREAM ─────────────────────────────────────────────────── */}
      {tab === 'stream' && (
        <>
          {!trace && !signature && (
            <ResponsiveFilters className="filter-bar" style={{ display: 'flex', gap: 8, padding: '10px 22px', borderBottom: '1px solid var(--line)', alignItems: 'center' }}>
              <Dropdown label="COMPONENT" value={component} options={COMPONENTS} onChange={setComponent} width={170} />
              <Dropdown label="LEVEL" value={level} options={LEVELS} onChange={setLevel} width={140} />
              <div className="chip-input" style={{ flex: '0 1 260px', minWidth: 160 }}>
                <span style={{ color: 'var(--ink-3)' }}>{Icons.search}</span>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="grep message…" />
              </div>
              {/* Free text, not a dropdown: error_kind is an open runtime set
                  (the API client mints `${prefix}_rejected` names), and a fixed
                  option list would silently hide whatever is not on it. */}
              <div
                className="chip-input"
                style={{
                  flex: '0 1 190px',
                  minWidth: 140,
                  ...(errorKindInvalid ? { borderColor: 'var(--bad)' } : {}),
                }}
                title={errorKindInvalid
                  ? 'Letters, digits, . - _ · max 64 — not sent until valid'
                  : 'Exact error kind, e.g. api_error'}
              >
                <span style={{ color: errorKindInvalid ? 'var(--bad)' : 'var(--ink-3)' }}>⚑</span>
                <input value={errorKind} onChange={(e) => setErrorKind(e.target.value)} placeholder="error kind…" />
              </div>
              <span className="muted mono" style={{ marginLeft: 'auto', fontSize: 10.5 }}>{displayed.length} shown · times {TZ_LABEL}</span>
            </ResponsiveFilters>
          )}
          {/* A 5s poll that has quietly started failing is indistinguishable
              from a quiet system, which is how a 403 read as "no logs". */}
          {logsQuery.isError && (
            <QueryErrorNote error={logsQuery.error} what="the log stream" onRetry={() => { void logsQuery.refetch(); }} />
          )}
          <div role="region" aria-label="Application log stream" tabIndex={0} className={'log-stream' + (trace && citedSet ? ' cp-stream-focus' : '')} ref={streamRef} style={{ padding: '0 8px' }}>
            {streamState === 'rows' ? (
              displayed.map((l, i) => (
                <LogLine key={l._id} log={l} expanded={openId === l._id} onToggle={() => setOpenId(openId === l._id ? null : l._id)} onTrace={drillTrace}
                  onErrorKind={(kind) => { setErrorKind(kind); setTrace(null); setSignature(null); }}
                  cite={trace && copilotOn && ex.open && !citeDegraded
                    ? { n: i + 1, cited: citedSet ? citedSet.has(i + 1) : false, flash: flashLine === i + 1 }
                    : null} />
              ))
            ) : streamState === 'error' ? null : (
              <div className="muted" style={{ padding: '30px 12px', fontSize: 12, fontStyle: 'italic', textAlign: 'center' }}>
                {streamState === 'loading'
                  ? 'Loading log stream…'
                  : streamState === 'empty-signature'
                    ? 'No log lines match this exact signature in the selected window.'
                    : 'No log lines in this window. Waiting for events…'}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── GROUPS (of the LEVEL filter's level) ───────────────────── */}
      {tab === 'groups' && (
        // A scroll container, so it takes focus and has a name: a keyboard
        // user scrolls a long list of groups from here.
        <div role="region" aria-label={groupsLabel} tabIndex={0} style={{ overflow: 'auto', padding: '0 22px' }}>
          {groupsQuery.isError && !groups.length ? (
            <QueryErrorNote error={groupsQuery.error} what="log groups" onRetry={() => { void groupsQuery.refetch(); }} />
          ) : groups.length === 0 ? (
            <div className="muted" style={{ padding: '30px 12px', fontSize: 12, fontStyle: 'italic', textAlign: 'center' }}>
              {groupsQuery.isPending ? 'Loading groups…' : groupsScope.empty}
            </div>
          ) : (
            <>
            {groupsTruncated && (
              <div className="muted" role="status" style={{ padding: '10px 12px', fontSize: 12 }}>
                Showing the {groups.length.toLocaleString()} most frequent groups; more exist in this window. Narrow it by level or time to see the rest.
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr>
                  <Th>Count</Th><Th>Level</Th><Th>Component</Th><Th style={{ width: '55%' }}>Sample</Th><Th>Last seen</Th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr
                    key={g.signature + g.component + g.level}
                    style={{ cursor: 'pointer', borderBottom: '1px solid var(--line-2)' }}
                    onClick={() => drillGroup(g)}
                    title="View exact signature instances in the stream"
                  >
                    <Td><span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{g.count.toLocaleString()}</span></Td>
                    <Td><Badge color={LEVEL_COLOR[g.level] ?? 'var(--ink-3)'}>{g.level}</Badge></Td>
                    <Td><span style={{ color: compColor(g.component) ?? 'var(--ink-2)' }}>{compLabel(g.component)}</span></Td>
                    {/* The row click is the mouse convenience; this button is
                        the drill-down a keyboard can reach. */}
                    <Td>
                      <button
                        type="button"
                        className="log-group-drill mono"
                        onClick={(event) => { event.stopPropagation(); drillGroup(g); }}
                        style={{ fontSize: 11.5, color: 'var(--ink-1)' }}
                      >{g.sample_message}</button>
                    </Td>
                    <Td><span className="muted">{g.last_ts ? fmtRelative(+new Date(g.last_ts)) : '—'}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>
      )}

      {/* ── HEALTH ─────────────────────────────────────────────────── */}
      {tab === 'health' && (
        <div style={{ padding: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          {/* `ingest` has no log stats (not a log producer) — its tile leans on
              the health summary's status + detail (e.g. "last scheduled run
              interrupted", "source 24h behind"). */}
          {['frontend', 'api_worker', 'pixel_worker', 'batch_worker', 'ingest'].map((id) => {
            const c = stats.components.find((x) => x.component === id);
            const h = healthComponents.find((x) => x.component === id);
            const ui = (h ? HEALTH_UI[h.status] : undefined) ?? HEALTH_QUIET;
            const color = ui.color;
            return (
              <div key={id} className="panel" style={{ padding: 14, borderLeft: `3px solid ${color}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
                  <span style={{ fontWeight: 700, fontFamily: 'var(--font-display)' }}>{compLabel(id)}</span>
                  <span className="muted mono" style={{ marginLeft: 'auto', fontSize: 10 }}>{ui.label}</span>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                  <HealthStat label="Events" value={(c?.total ?? 0).toLocaleString()} />
                  <HealthStat label="Errors" value={(c?.errors ?? 0).toLocaleString()} color={(c?.errors ?? 0) > 0 ? 'var(--bad)' : undefined} />
                  <HealthStat label="Warns" value={(c?.warns ?? 0).toLocaleString()} color={(c?.warns ?? 0) > 0 ? 'var(--warn)' : undefined} />
                </div>
                <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
                  {(h ? componentDetail(h.component, h.detail) : null) ?? (c?.last_ts ? `Last event: ${fmtRelative(+new Date(c.last_ts))}` : 'Last event: none in window')}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── ARCHIVE (the cold store) ─────────────────────────────────── */}
      {tab === 'archive' && (
        <div className="log-archive-layout" style={{ display: 'flex', minHeight: 0, flex: 1 }}>
          {/* File list for the chosen day */}
          <div style={{ width: 320, borderRight: '1px solid var(--line)', overflow: 'auto', padding: 14 }}>
            <div className="field-lbl mb-2">Archived logs</div>
            <div className="field-hint" style={{ marginBottom: 8 }}>
              Long-term log snapshots in file storage, beyond the hot retention window.
            </div>
            <input
              type="date"
              aria-label="Archive date"
              className="input mono"
              style={{ width: '100%', fontSize: 12, marginBottom: 10 }}
              value={archiveDate}
              onChange={(e) => { setArchiveDate(e.target.value); setArchiveKey(null); }}
            />
            <select
              className="input mono"
              aria-label="Archive hour"
              style={{ width: '100%', fontSize: 12, marginBottom: 10 }}
              value={archiveHour}
              onChange={(e) => { setArchiveHour(e.target.value); setArchiveKey(null); }}
            >
              <option value="">Whole day</option>
              {Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0')).map((hh) => (
                <option key={hh} value={hh}>{hh}:00 UTC</option>
              ))}
            </select>
            {archiveTruncated && (
              <div className="muted" role="status" style={{ fontSize: 11.5, padding: '0 2px 10px' }}>
                {archiveHour
                  ? 'This hour holds more than 1,000 archived files; the list shows an arbitrary subset of them.'
                  : 'This day holds more than 1,000 archived files; the list shows the earliest hours only. Pick an hour to see the rest.'}
              </div>
            )}
            {archiveList.length === 0 ? (
              <div className="muted" style={{ fontSize: 11.5, fontStyle: 'italic', padding: '12px 2px' }}>
                No archived files for this day.
              </div>
            ) : (
              archiveList.map((f) => {
                const parts = f.key.split('/');
                const hh = parts[4] ?? '';
                const short = parts[parts.length - 1]?.slice(0, 8) ?? f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setArchiveKey(f.key)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                      padding: '6px 8px', marginBottom: 4, borderRadius: 'var(--r-sm)',
                      border: '1px solid ' + (archiveKey === f.key ? 'var(--accent, #6aa9ff)' : 'var(--line)'),
                      background: archiveKey === f.key ? 'color-mix(in oklch, #6aa9ff 10%, var(--panel))' : 'var(--panel)',
                      fontFamily: 'var(--font-mono)', fontSize: 11,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{hh}:00 · {short}</div>
                    <div className="muted" style={{ fontSize: 10 }}>
                      {(f.size / 1024).toFixed(1)} KB{f.uploaded ? ` · ${fmtRelative(+new Date(f.uploaded))}` : ''}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {/* Selected file's lines */}
          <div role="region" aria-label="Archived log stream" tabIndex={0} className="log-stream" style={{ flex: 1, padding: '0 8px' }}>
            {!archiveKey ? (
              <div className="muted" style={{ padding: '30px 12px', fontSize: 12, fontStyle: 'italic', textAlign: 'center' }}>
                Select an archived file to view its log lines.
              </div>
            ) : archiveObjQuery.isError ? (
              <QueryErrorNote error={archiveObjQuery.error} what="this archived file" onRetry={() => { void archiveObjQuery.refetch(); }} />
            ) : archiveLines.length === 0 ? (
              /* Was "Loading… (or the file is empty)" — one string standing in
                 for three different states, none of which it answered. */
              <div className="muted" style={{ padding: '30px 12px', fontSize: 12, fontStyle: 'italic', textAlign: 'center' }}>
                {archiveObjQuery.isPending ? 'Loading the archived file…' : 'This archived file is empty.'}
              </div>
            ) : (
              archiveLines.map((l) => (
                <LogLine key={l._id} log={l} expanded={openId === l._id} onToggle={() => setOpenId(openId === l._id ? null : l._id)} onTrace={drillTrace} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── small presentational helpers ─────────────────────────────────────────────
interface KpiProps {
  label: string;
  value: string;
  color?: string;
}
function Kpi({ label, value, color }: KpiProps) {
  return (
    <div className="kpi" style={{ minWidth: 104 }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-val" style={{ color: color ?? 'var(--ink-1)' }}>{value}</div>
    </div>
  );
}
interface HealthStatProps {
  label: string;
  value: string;
  color?: string | undefined;
}
function HealthStat({ label, value, color }: HealthStatProps) {
  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)', color: color ?? 'var(--ink-1)' }}>{value}</div>
      <div className="muted" style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
    </div>
  );
}
const thStyle: CSSProperties = { textAlign: 'left', fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-3)', padding: '10px 8px', borderBottom: '1px solid var(--line)', position: 'sticky', top: 0, background: 'var(--bg-elev)' };
const tdStyle: CSSProperties = { padding: '9px 8px', verticalAlign: 'top' };
interface CellProps {
  children: ReactNode;
  style?: CSSProperties;
}
const Th = ({ children, style }: CellProps) => <th style={{ ...thStyle, ...style }}>{children}</th>;
const Td = ({ children, style }: CellProps) => <td style={{ ...tdStyle, ...style }}>{children}</td>;
interface BadgeProps {
  children: ReactNode;
  color: string;
}
function Badge({ children, color }: BadgeProps) {
  return (
    <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 4, fontSize: 10, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.06em', color, border: `1px solid ${color}`, background: `color-mix(in oklch, ${color} 10%, transparent)` }}>{children}</span>
  );
}

// ── one log line ─────────────────────────────────────────────────────────────
// `cite` (copilot trace narration only): { n, cited, flash } — the rendered
// line number for `lN` refs, claim-hover highlight, and chip-click flash.
interface LogCite {
  n: number;
  cited: boolean;
  flash: boolean;
}
interface LogLineProps {
  log: AppLogLine;
  expanded: boolean;
  onToggle: () => void;
  onTrace: (traceId: string) => void;
  cite?: LogCite | null;
  onErrorKind?: ((kind: string) => void) | null;
}
function LogLine({ log, expanded, onToggle, onTrace, cite = null, onErrorKind = null }: LogLineProps) {
  const ts = fmtClockMs(log.ts);
  const sourceColor = compColor(log.component) ?? 'var(--ink-3)';
  const ctx = parseLogContext(log.context_json);
  const traceId = log.trace_id;
  const errorKind = log.error_kind;
  const cls = `log-line log-lvl-${log.level}`
    + (cite ? ' cp-cite' : '')
    + (cite?.cited ? ' cp-cite-hi' : '')
    + (cite?.flash ? ' cp-cite-flash' : '');
  return (
    <div className={cls} data-line={cite ? cite.n : undefined} onClick={onToggle}>
      <span className="log-ts">{ts}</span>
      <span className={`log-level log-level-${log.level}`}>{(log.level || '').toUpperCase().padEnd(5, ' ')}</span>
      <span className="log-source" style={{ color: sourceColor, minWidth: 92, display: 'inline-block' }}>{compLabel(log.component)}</span>
      <LogRequest log={log} />
      <button type="button" className="log-expand btn btn-sm" aria-expanded={expanded} onClick={event=>{event.stopPropagation();onToggle();}}>{expanded ? 'Hide details' : 'Details'} <span aria-hidden="true">{expanded ? '−' : '+'}</span></button>
      {/* The trace chip belongs inside the message cell. As a sibling it was a
          5th child of a 4-track grid and wrapped onto its own row under the
          timestamp. */}
      <span className="log-msg">
        {log.message}
        {traceId && (
          <span
            className="mono"
            role="button" tabIndex={0} aria-label="Follow this trace across components"
            onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();e.stopPropagation();onTrace(traceId);}}}
            onClick={(e) => { e.stopPropagation(); onTrace(traceId); }}
            title="Follow this trace across components"
            style={{ marginLeft: 8, fontSize: 10, color: 'var(--ink-3)', textDecoration: 'underline', cursor: 'pointer' }}
          >
            ⇄ {traceId.slice(0, 8)}
          </span>
        )}
      </span>
      {expanded && (
        <div className="log-detail">
          {log.route && <Row k="route" v={log.route} />}
          {log.http_status != null && <Row k="status" v={String(log.http_status)} />}
          {log.latency_ms != null && <Row k="latency" v={`${log.latency_ms} ms`} />}
          {errorKind && (
            <Row
              k="error_kind"
              v={onErrorKind ? (
                <span
                  role="button" tabIndex={0}
                  onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();e.stopPropagation();onErrorKind(errorKind);}}}
                  onClick={(e) => { e.stopPropagation(); onErrorKind(errorKind); }}
                  title="Filter the stream to this error kind"
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                >{errorKind}</span>
              ) : errorKind}
            />
          )}
          {traceId && <Row k="trace_id" v={traceId} />}
          {log.user_id && <Row k="user_id" v={log.user_id} />}
          {log.release && <Row k="release" v={log.release} />}
          {log.signature && <Row k="signature" v={log.signature} />}
          <Row k="ts" v={fmtStampEt(log.ts)} />
          {log.stack_stub && (
            <div style={{ marginTop: 6 }}>
              <div className="muted mono" style={{ fontSize: 9.5, textTransform: 'uppercase', marginBottom: 3 }}>stack</div>
              <pre className="mono" style={{ fontSize: 10.5, whiteSpace: 'pre-wrap', margin: 0, color: 'var(--ink-2)' }}>{log.stack_stub}</pre>
            </div>
          )}
          {ctx && ctx.breadcrumbs.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div className="muted mono" style={{ fontSize: 9.5, textTransform: 'uppercase', marginBottom: 3 }}>breadcrumbs</div>
              {ctx.breadcrumbs.map((b, i) => (
                <div key={i} className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>· <b style={{ color: 'var(--ink-2)' }}>{b.kind}</b> {b.msg}</div>
              ))}
            </div>
          )}
          {/* Any other structured fields (e.g. `error`, `rule` from the batch relay). */}
          {ctx?.fields.map(([k, v]) => (
            <div key={k} style={{ marginTop: 6 }}>
              <div className="muted mono" style={{ fontSize: 9.5, textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
              <pre className="mono" style={{ fontSize: 10.5, whiteSpace: 'pre-wrap', margin: 0, color: 'var(--ink-2)' }}>{v}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
interface RowProps {
  k: string;
  v: ReactNode;
}
function Row({ k, v }: RowProps) {
  return (
    <div className="log-detail-row">
      <span className="log-detail-k">{k}</span>
      <span className="log-detail-v">{v}</span>
    </div>
  );
}

export { LogsView };
